/* eslint-disable react/jsx-no-leaked-render */
/* eslint-disable indent */
/* eslint-disable no-mixed-spaces-and-tabs */
import React, { useCallback, useState } from 'react';
import { BoxProps, Flex, VStack } from '@chakra-ui/react';
import { useLocation } from 'react-router-dom';
import { hasNavItemClaim, LinkItems, SUPER_USER_ID } from '../../../constants';
import { NavItem } from './NavItem';
import { ExpandedButton } from './ExpandedButton';
import { Claim, CurrentUserType, ModulesEnum, Role } from '../../../types';
import { useVideoInstructionStore } from '../../../store';

import { DealFinder, S8MainLogo, S8SmallLogo } from '../../../assets';
import { SideBarFooter } from './SideBarFooter';
import {
	hasCoachingAccess,
	hasCommunityAccess,
	isLimitedGuestRole,
	useSignOut,
} from '../../../utils';
import { NavAccordion } from '../../NavAccordion/NavAccordion';
import { CruzButton } from './CruzButton';
import {
	getGuestNavigationModalTarget,
	GuestNavigationModal,
	GuestNavigationModalTarget,
} from './GuestNavigationModal';

//TODO refactor getFilteredNavLinks

interface SidebarContentProps extends BoxProps {
	onClose: VoidFunction;
	menuExpanded: boolean;
	toggleMenu: VoidFunction;
	data?: CurrentUserType;
	isLoading: boolean;
	amountUnreadAdminMessages: number;
	amountUnreadCommunity: number;
}

export const SidebarContent = ({
	menuExpanded,
	toggleMenu,
	data,
	isLoading,
	amountUnreadCommunity,
	amountUnreadAdminMessages,
	...rest
}: SidebarContentProps) => {
	const location = useLocation();

	const onSignOut = useSignOut();

	const { isCommunityAvailable, isCommunityInstructionCompleted } =
		data || {};

	const fullName = `${data?.firstName} ${data?.lastName}`;
	const claimsIds = data?.claimsIds || [];
	const isAdmin = data?.roleId === Role.Admin;
	const isWebinarGuest = data?.roleId === Role.Guest;
	const modulesIds = data?.modulesIds || [];
	const hasExpertNetwork = modulesIds.includes(ModulesEnum.ExpertNetwork);
	const hasCruzAccess =
		claimsIds.includes(Claim.CruzControl) &&
		modulesIds.includes(ModulesEnum.DealTools);
	const [guestModalTarget, setGuestModalTarget] =
		useState<GuestNavigationModalTarget | null>(null);
	const { onOpen } = useVideoInstructionStore();

	// const { amountUnreadAdminMessages, amountCommunityUnreadChats } =
	// 	useUnreadSidebarCounterStore(state => state);

	const getFilteredNavLinks = useCallback(() => {
		return LinkItems.filter(elem => {
			if ('adminOnly' in elem && elem.adminOnly && !isAdmin) {
				return false;
			}
			if (elem.guestRestricted && isLimitedGuestRole(data?.roleId)) {
				return false;
			}
			if (
				elem.id === Claim.Courses &&
				!claimsIds.includes(Claim.Courses)
			) {
				return false;
			}
			if (elem.id === Claim.Messenger) {
				return isWebinarGuest || hasCommunityAccess(claimsIds);
			}
			if (elem.id === Claim.Coaching) {
				return isWebinarGuest || hasCoachingAccess(claimsIds);
			}

			if (
				elem.id === Claim.Vendors &&
				!isWebinarGuest &&
				!claimsIds.includes(Claim.Vendors)
			) {
				return false;
			}

			if (elem.id === Claim.Vault && !claimsIds.includes(Claim.Vault)) {
				return false;
			}

			if (elem.id === Claim.TenantFinder && !isAdmin) {
				return false;
			}

			return true;
		});
	}, [claimsIds, isAdmin, isWebinarGuest]);

	const accordionItems = getFilteredNavLinks().filter(item =>
		Boolean(item.accordion),
	);

	const isSuperAdmin = data?.userId === SUPER_USER_ID;

	return (
		<Flex
			direction="column"
			align="space-between"
			py={'16px'}
			bg="blue.100"
			transition=".3s"
			w={{ base: 'full', md: menuExpanded ? '250px' : '120px' }}
			h="100dvh"
			pos="fixed"
			zIndex={'99'}
			{...rest}>
			<Flex alignItems={'center'} justifyContent={'center'}>
				{menuExpanded ? <S8MainLogo /> : <S8SmallLogo />}
			</Flex>
			<Flex
				flexDirection={'column'}
				px={'10px'}
				overflowX={'hidden'}
				direction={'column'}
				className={'custom-scroll'}>
				<VStack flexGrow={1} mt={'26px'}>
					{getFilteredNavLinks()
						.filter(item => item.text === 'Quick Start')
						.map(item => (
							<NavItem
								isActive={location.pathname === item.link}
								key={item.text}
								icon={item.icon}
								link={item.link}
								isDisabled={false}
								isInAccordion={false}
								menuExpanded={menuExpanded}
								heightPadding={'16px'}
								optionalClassName={item.optionalClassName}>
								{item.text}
							</NavItem>
						))}
					{accordionItems.length ? (
						<NavAccordion
							className="guide-deal-finder"
							icon={<DealFinder />}
							title={'Deal Finder'}
							menuExpanded={menuExpanded}>
							{accordionItems.map(item => {
								const target = getGuestNavigationModalTarget(
									item.id,
								);
								const hasItemAccess =
									hasNavItemClaim(item, claimsIds) &&
									(item.id !== Claim.Vendors ||
										hasExpertNetwork);
								const isGuestLocked =
									isWebinarGuest &&
									target !== null &&
									!hasItemAccess;

								return (
									<NavItem
										onAction={
											isGuestLocked
												? () =>
														setGuestModalTarget(
															target,
														)
												: !hasNavItemClaim(
														item,
														claimsIds,
												  )
												? onOpen
												: undefined
										}
										isActive={
											location.pathname === item.link
										}
										key={item.text}
										icon={item.icon}
										link={item.link}
										isDisabled={!hasItemAccess}
										showDisabledStyle={!isGuestLocked}
										menuExpanded={menuExpanded}
										heightPadding={'8px'}>
										{item.text}
									</NavItem>
								);
							})}
						</NavAccordion>
					) : null}

					{getFilteredNavLinks()
						.filter(
							item =>
								Boolean(!item.accordion) &&
								item.text !== 'Quick Start',
						)
						.map(item => {
							const isCoachingTab = item.id === Claim.Coaching;
							const isVaultTab = item.id === Claim.Vault;

							const isTenantFinder =
								item.id === Claim.TenantFinder;

							if (isSuperAdmin && (isCoachingTab || isVaultTab)) {
								return null;
							}

							const isCommunity = item.text === 'Community';
							const target = getGuestNavigationModalTarget(
								item.id,
							);
							const isAdminOnly =
								'adminOnly' in item && item.adminOnly;
							const hasItemAccess = isAdminOnly
								? isAdmin
								: isCommunity
								? hasCommunityAccess(claimsIds)
								: isCoachingTab
								? hasCoachingAccess(claimsIds)
								: claimsIds.includes(item.id);
							const hasGuestAccess =
								hasItemAccess &&
								hasExpertNetwork &&
								(!isCommunity ||
									Boolean(isCommunityAvailable)) &&
								(!isCoachingTab ||
									Boolean(
										isCommunityAvailable &&
											isCommunityInstructionCompleted,
									));
							const isGuestLocked =
								isWebinarGuest &&
								target !== null &&
								!hasGuestAccess;
							const link = isCommunity
								? !isCommunityInstructionCompleted
									? '/instruction'
									: item.link
								: item.link;

							return (
								<NavItem
									onAction={
										isGuestLocked
											? () => setGuestModalTarget(target)
											: !hasItemAccess && item.id > 0
											? onOpen
											: undefined
									}
									isActive={location.pathname === link}
									key={item.text}
									icon={item.icon}
									avatar={item.avatar}
									link={link}
									showAvailableBadge={
										isCommunity &&
										isCommunityAvailable &&
										!isCommunityInstructionCompleted
									}
									isDisabled={
										isGuestLocked
											? true
											: isCommunity
											? !isCommunityAvailable
											: isCoachingTab
											? !isCommunityInstructionCompleted ||
											  !isCommunityAvailable
											: isTenantFinder
											? !hasItemAccess
											: Boolean(
													!hasItemAccess ||
														item.disabledBadgeText,
											  )
										// true
										// (permissionId === UserPermission.CourseOnly &&
										// 	item.link !== '/courses') ||
										// isLoading
									}
									showDisabledStyle={!isGuestLocked}
									disabledBadgeText={item.disabledBadgeText}
									menuExpanded={menuExpanded}
									isInAccordion={item.accordion}
									unreadCount={
										isCoachingTab
											? amountUnreadAdminMessages
											: isCommunity
											? amountUnreadCommunity
											: undefined
									}
									heightPadding={'16px'}
									optionalClassName={item.optionalClassName}>
									{item.text}
								</NavItem>
							);
						})}
				</VStack>
			</Flex>
			<VStack mt={'auto'} px={'10px'} spacing={'8px'} align={'stretch'}>
				{isWebinarGuest || claimsIds.includes(Claim.CruzControl) ? (
					<CruzButton
						menuExpanded={menuExpanded}
						hasAccess={hasCruzAccess}
					/>
				) : null}
				{/*FOOTER*/}
				<SideBarFooter
					isLoading={isLoading}
					data={data}
					menuExpanded={menuExpanded}
					fullName={fullName}
					onSignOut={onSignOut}
				/>
			</VStack>
			<ExpandedButton menuExpanded={menuExpanded} onClick={toggleMenu} />
			<GuestNavigationModal
				target={guestModalTarget}
				onClose={() => setGuestModalTarget(null)}
			/>
		</Flex>
	);
};
